import React, { useState } from "react";
import axios from "axios";
import { Star } from "lucide-react";
import { toast } from "sonner";
import { adminAuthHeader } from "@/hooks/useAdminAuth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Interactive 0..5 star rating for CRM urgency. Persists via
 * PATCH /admin/users/{id}/urgency-stars — click a star to set that
 * level; click the currently-selected star to clear back to 0.
 *
 * Renders inline in the funnel table row; parent MUST wrap it in a
 * TableCell that stops row-click propagation, else clicking a star
 * would also navigate to the detail page.
 */
export default function UrgencyStarsPicker({ userId, value = 0, onChange }) {
  const current = Number.isFinite(+value) ? Math.max(0, Math.min(5, +value)) : 0;
  const [pending, setPending] = useState(null);  // the star being written to server
  const [hover, setHover] = useState(0);

  const setStars = async (n) => {
    if (pending !== null) return;
    // Clicking the same star clears it (toggle-to-0 UX — standard for star ratings).
    const next = n === current ? 0 : n;
    setPending(next);
    try {
      await axios.patch(`${API}/admin/users/${userId}/urgency-stars`,
        { stars: next }, { headers: adminAuthHeader() });
      onChange?.(userId, next);
      if (next === 0) toast.success("Urgenza rimossa");
      else toast.success(`Urgenza: ${next}★`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore salvataggio stelline");
    } finally {
      setPending(null);
    }
  };

  const displayed = hover || (pending ?? current);

  return (
    <div
      className="inline-flex items-center gap-0.5"
      data-testid={`urgency-stars-${userId}`}
      onMouseLeave={() => setHover(0)}
    >
      {[1, 2, 3, 4, 5].map((n) => {
        const on = n <= displayed;
        return (
          <button
            key={n}
            type="button"
            onClick={() => setStars(n)}
            onMouseEnter={() => setHover(n)}
            disabled={pending !== null}
            aria-label={`${n} stelle`}
            data-testid={`urgency-star-${userId}-${n}`}
            className="w-5 h-5 inline-flex items-center justify-center rounded transition disabled:opacity-40 hover:scale-110"
            title={n === current ? "Clicca per rimuovere" : `Imposta a ${n}★`}
          >
            <Star
              size={13}
              strokeWidth={1.6}
              style={{
                color: on ? "#f59e0b" : "#cbd5e1",
                fill: on ? "#f59e0b" : "transparent",
              }}
            />
          </button>
        );
      })}
    </div>
  );
}
